<template>
    <!-- "Draft a Document" chooser (RAC-10324) — extraction of the tab's inline
         Create-Lease chooser (RAC-10253), grown to four tiles: lease, upload,
         eviction notice (wired live in RAC-10343), and the general-form
         catalog. -->
    <ModalJetstream :show="show" :max-width="'2xl'" @close="$emit('close')">
        <div class="bg-white rounded-lg p-6 m-2">
            <div class="mb-5 flex items-start justify-between border-b pb-3">
                <div>
                    <h2 class="text-xl font-bold text-gray-800">Draft a Document</h2>
                    <p class="text-sm text-gray-500 mt-0.5">
                        Start with a Lease, Eviction Notice, or explore other related documents.
                    </p>
                </div>
                <button class="text-gray-500 hover:text-gray-700 focus:outline-none" @click="$emit('close')">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <!-- Start New Lease -->
                <button
                    type="button"
                    :disabled="!!newLeaseRefusal"
                    :class="tileClass(newLeaseRefusal)"
                    @click="$emit('start-lease')"
                >
                    <div class="w-11 h-11 bg-rent-risk-blue-100 rounded-lg flex items-center justify-center mb-3">
                        <svg class="w-5 h-5 text-rent-risk-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    </div>
                    <p class="text-base font-bold text-gray-900">Start New Lease</p>
                    <p class="text-sm text-gray-500 mt-1">Answer a few questions and build a state-specific lease, ready for e-signature.</p>
                    <!-- The server's own words, so the tile can never explain the
                         refusal differently from the endpoint (RAC-10257). -->
                    <p v-if="newLeaseRefusal" class="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-2">{{ newLeaseRefusal }}</p>
                </button>

                <!-- Upload Existing Lease -->
                <button
                    type="button"
                    :disabled="!!uploadRefusal"
                    :class="tileClass(uploadRefusal)"
                    @click="$emit('upload-lease')"
                >
                    <div class="w-11 h-11 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
                        <svg class="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                    </div>
                    <p class="text-base font-bold text-gray-900">Upload Existing Lease</p>
                    <p class="text-sm text-gray-500 mt-1">Already have a lease? Upload your PDF or document to keep it on file here.</p>
                    <p v-if="uploadRefusal" class="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-2">{{ uploadRefusal }}</p>
                </button>

                <!-- Eviction Notice (RAC-10343) -->
                <button
                    type="button"
                    class="flex flex-col items-start text-left rounded-xl border-2 border-gray-200 p-5 hover:border-rent-risk-blue-400 hover:bg-gray-50 transition-colors focus:outline-none"
                    @click="$emit('eviction-notice')"
                >
                    <div class="w-11 h-11 bg-amber-100 rounded-lg flex items-center justify-center mb-3">
                        <svg class="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                    </div>
                    <p class="text-base font-bold text-gray-900">Eviction Notice</p>
                    <p class="text-sm text-gray-500 mt-1">Need to serve an eviction notice? Start here.</p>
                    <p class="text-xs text-gray-400 mt-2">Not legal advice.</p>
                </button>

                <!-- Explore Documents -->
                <button
                    type="button"
                    class="flex flex-col items-start text-left rounded-xl border-2 border-gray-200 p-5 hover:border-rent-risk-blue-400 hover:bg-gray-50 transition-colors focus:outline-none"
                    @click="$emit('explore-documents')"
                >
                    <div class="w-11 h-11 bg-emerald-100 rounded-lg flex items-center justify-center mb-3">
                        <svg class="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                    </div>
                    <p class="text-base font-bold text-gray-900">Explore Documents</p>
                    <p class="text-sm text-gray-500 mt-1">Take advantage of RentRisk's {{ catalogCount }}+ real estate forms.</p>
                </button>
            </div>
        </div>
    </ModalJetstream>
</template>

<script setup>
import ModalJetstream from '@/Jetstream/Modal.vue'

defineProps({
    show: { type: Boolean, default: false },
    catalogCount: { type: Number, default: 30 },
    // One lease per property (RAC-10257). Each is the server's refusal message
    // for that path, or null when it is allowed — the tile renders whatever the
    // endpoint would have said, so the two cannot drift apart.
    uploadRefusal: { type: String, default: null },
    newLeaseRefusal: { type: String, default: null },
})

defineEmits(['close', 'start-lease', 'upload-lease', 'eviction-notice', 'explore-documents'])

const TILE_BASE = 'flex flex-col items-start text-left rounded-xl border-2 p-5 transition-colors focus:outline-none'

const tileClass = (refusal) => [
    TILE_BASE,
    refusal
        ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
        : 'border-gray-200 hover:border-rent-risk-blue-400 hover:bg-gray-50',
]
</script>
